/**
 * Widget interface language (toolbar, panels, messages).
 *
 * Strings are written in English in the code and wrapped in t(); the English
 * text is the key. Translations live in src/widget/i18n/locales/<code>.json,
 * generated by `npm run i18n:widget` and served by GET /api/v1/widget/i18n/<code>.
 *
 * State lives on a window global so the core and the lazy extras bundle share it.
 */

/** Interface languages: code → native name. 'en' is built in. */
export const UI_LANGUAGES: Record<string, string> = {
  en: 'English',
  ar: 'العربية',
  da: 'Dansk',
  de: 'Deutsch',
  es: 'Español',
  fa: 'فارسی',
  fi: 'Suomi',
  fr: 'Français',
  hi: 'हिन्दी',
  hmn: 'Hmoob',
  ht: 'Kreyòl ayisyen',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  nb: 'Norsk',
  nl: 'Nederlands',
  pl: 'Polski',
  ps: 'پښتو',
  pt: 'Português',
  ru: 'Русский',
  so: 'Soomaali',
  sv: 'Svenska',
  tl: 'Tagalog',
  tr: 'Türkçe',
  uk: 'Українська',
  ur: 'اردو',
  vi: 'Tiếng Việt',
  zh: '中文（简体）',
  'zh-TW': '中文（繁體）',
};

const RTL = new Set(['ar', 'fa', 'ps', 'ur']);

/** Visitor's own choice (Settings → Toolbar language) */
const STORAGE_KEY = 'rv-ui-lang';
const CACHE_PREFIX = 'rv-ui-strings-';

type StringsLoader = (lang: string) => Promise<Record<string, string>>;

interface I18nState {
  lang: string;
  strings: Record<string, string>;
  version?: string;
  loader?: StringsLoader;
}

function state(): I18nState {
  const w = window as unknown as { __ReadingVoxI18n?: I18nState };
  if (!w.__ReadingVoxI18n) w.__ReadingVoxI18n = { lang: 'en', strings: {} };
  return w.__ReadingVoxI18n;
}

/** Map a BCP 47 tag (es-MX, zh-Hant, pt-BR, no) to a supported interface language */
export function matchUiLanguage(tag: string | null | undefined): string | null {
  if (!tag) return null;
  const lower = tag.trim().toLowerCase().replace('_', '-');
  if (!lower) return null;
  if (lower === 'zh-tw' || lower === 'zh-hk' || lower === 'zh-mo' || lower.startsWith('zh-hant')) return 'zh-TW';
  const base = lower.split('-')[0];
  if (base === 'no' || base === 'nn') return 'nb';
  if (base === 'fil') return 'tl';
  if (base === 'prs') return 'fa'; // Dari
  return base in UI_LANGUAGES ? base : null;
}

/**
 * Order: the visitor's saved choice → the site's data-ui-lang → the visitor's
 * browser language → the page language → English.
 */
export function resolveUiLanguage(configured?: string): string {
  let saved: string | null = null;
  try { saved = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
  const candidates = [saved, configured, ...(navigator.languages || [navigator.language]), document.documentElement.lang];
  for (const c of candidates) {
    const match = matchUiLanguage(c);
    if (match) return match;
  }
  return 'en';
}

export function saveUiLanguage(lang: string): void {
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
}

export function getUiLanguage(): string {
  return state().lang;
}

/** 'rtl' for Arabic, Persian, Pashto, Urdu — set on panels with running text */
export function uiDir(): 'rtl' | 'ltr' {
  return RTL.has(state().lang) ? 'rtl' : 'ltr';
}

/**
 * Marks a string in a data table (labels rendered later with t(label)) so the
 * extraction script finds it. Returns the string unchanged.
 */
export function N_(text: string): string {
  return text;
}

/**
 * Translate an interface string. `{name}` placeholders are filled from vars.
 * Falls back to the English text when there's no translation.
 */
export function t(text: string, vars?: Record<string, string | number>): string {
  let out = state().strings[text] || text;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

/**
 * Load a language's strings (from local storage, else `fetchStrings`).
 * Never throws; on failure the interface stays in English.
 */
export async function loadUiLanguage(
  lang: string,
  version: string,
  fetchStrings: StringsLoader
): Promise<void> {
  const s = state();
  s.version = version;
  s.loader = fetchStrings;
  if (lang === 'en') { s.lang = 'en'; s.strings = {}; return; }

  const cacheKey = `${CACHE_PREFIX}${lang}`;
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (cached?.v === version && cached.strings) {
      s.lang = lang; s.strings = cached.strings;
      return;
    }
  } catch { /* ignore */ }

  try {
    const strings = await fetchStrings(lang);
    if (!strings || typeof strings !== 'object') return;
    s.lang = lang; s.strings = strings;
    try { localStorage.setItem(cacheKey, JSON.stringify({ v: version, strings })); } catch { /* ignore */ }
  } catch { /* stay in English */ }
}

/** Visitor picked a language in Settings: save it and load its strings */
export async function switchUiLanguage(lang: string): Promise<void> {
  const s = state();
  saveUiLanguage(lang);
  if (!s.loader) { if (lang === 'en') { s.lang = 'en'; s.strings = {}; } return; }
  await loadUiLanguage(lang, s.version || '', s.loader);
}
